import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { getApiBaseUrl } from '../../apiBase';
import LocationMap from './LocationMap';
import {
    AddressProvidersBanner,
    AddressSourceBadge,
    StreetHouseLine
} from './AddressSourceBanner';

function useDebounce(value, delay) {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(timer);
    }, [value, delay]);
    return debounced;
}

const MOSCOW = [55.7558, 37.6173];

function haversineMeters(lat1, lng1, lat2, lng2) {
    const R = 6371000;
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2
        + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Умный ввод адреса: Photon (через API) + карта Leaflet/OSM.
 */
export default function AddressInput({
    address = '',
    lat = null,
    lng = null,
    city = '',
    onChange,
    placeholder,
    inputStyle = {},
    disabled = false,
    geocodeOnLoad = true
}) {
    const { t } = useTranslation('school');
    const inputPlaceholder = placeholder ?? t('addressInput.placeholder');
    const [query, setQuery] = useState(address || '');
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [mapLat, setMapLat] = useState(lat);
    const [mapLng, setMapLng] = useState(lng);
    const [mapCenter, setMapCenter] = useState(MOSCOW);
    const [geocodingMap, setGeocodingMap] = useState(false);
    const [reverseHint, setReverseHint] = useState('');
    const [providers, setProviders] = useState({ dadataActive: false, photonConfigured: true });
    const [providerStatus, setProviderStatus] = useState('loading');
    const [lastSource, setLastSource] = useState(null);
    const [streetLine, setStreetLine] = useState({ street: null, houseNumber: null });
    const wrapRef = useRef(null);
    const initialGeocodeDone = useRef(false);
    const reverseTimerRef = useRef(null);
    const reverseSeqRef = useRef(0);
    const suppressSuggestRef = useRef(false);
    const debouncedQuery = useDebounce(query, 300);

    const api = useMemo(() => {
        const token = localStorage.getItem('token');
        return axios.create({
            baseURL: getApiBaseUrl(),
            headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get('/address/providers');
                if (cancelled) return;
                const d = res.data || {};
                const dadataActive = !!(d.dadataActive ?? d.DadataActive);
                const photonConfigured = d.photonConfigured ?? d.PhotonConfigured ?? true;
                setProviders({ dadataActive, photonConfigured });
                setProviderStatus(dadataActive ? 'active' : 'inactive');
            } catch {
                if (!cancelled) setProviderStatus('unreachable');
            }
        })();
        return () => { cancelled = true; };
    }, [api]);

    useEffect(() => {
        setQuery(address || '');
    }, [address]);

    useEffect(() => {
        setMapLat(lat);
        setMapLng(lng);
        if (lat != null && lng != null)
            setMapCenter([Number(lat), Number(lng)]);
    }, [lat, lng]);

    useEffect(() => {
        if (!city?.trim()) return;
        let cancelled = false;
        (async () => {
            try {
                const geo = await api.get('/address/geocode', {
                    params: { address: `${city.trim()}, Россия` }
                });
                if (!cancelled && geo.data?.success && geo.data.lat != null) {
                    setMapCenter([Number(geo.data.lat), Number(geo.data.lng)]);
                }
            } catch { /* ignore */ }
        })();
        return () => { cancelled = true; };
    }, [city, api]);

    useEffect(() => {
        if (!geocodeOnLoad || disabled || initialGeocodeDone.current) return;
        if (mapLat != null && mapLng != null) {
            initialGeocodeDone.current = true;
            return;
        }
        const addr = (address || query || '').trim();
        if (addr.length < 5) return;

        let cancelled = false;
        (async () => {
            setGeocodingMap(true);
            try {
                const searchAddr = city?.trim() ? `${addr}, ${city.trim()}` : addr;
                const geo = await api.get('/address/geocode', { params: { address: searchAddr } });
                if (cancelled) return;
                if (geo.data?.success && geo.data.lat != null && geo.data.lng != null) {
                    const newLat = Number(geo.data.lat);
                    const newLng = Number(geo.data.lng);
                    setMapLat(newLat);
                    setMapLng(newLng);
                    setMapCenter([newLat, newLng]);
                    if (geo.data.source) setLastSource(geo.data.source);
                    onChange?.({
                        address: geo.data.formattedAddress || addr,
                        addressFull: geo.data.formattedAddress || addr,
                        lat: newLat,
                        lng: newLng,
                        city: geo.data.city || city,
                        region: geo.data.region,
                        postalCode: geo.data.postalCode
                    });
                    if (geo.data.formattedAddress)
                        setQuery(geo.data.formattedAddress);
                }
            } catch { /* ignore */ }
            finally {
                if (!cancelled) {
                    setGeocodingMap(false);
                    initialGeocodeDone.current = true;
                }
            }
        })();
        return () => { cancelled = true; };
    }, [address, city, geocodeOnLoad, disabled, mapLat, mapLng, api, onChange, query]);

    useEffect(() => {
        function onDocClick(e) {
            if (wrapRef.current && !wrapRef.current.contains(e.target))
                setOpen(false);
        }
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, []);

    useEffect(() => {
        if (suppressSuggestRef.current) {
            suppressSuggestRef.current = false;
            setSuggestions([]);
            return;
        }

        if (disabled || debouncedQuery.trim().length < 2) {
            setSuggestions([]);
            return;
        }

        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const params = { q: debouncedQuery.trim() };
                if (city?.trim()) params.city = city.trim();
                const res = await api.get('/address/suggest', { params });
                if (!cancelled) {
                    const list = res.data || [];
                    setSuggestions(list);
                    setOpen(true);
                    if (list.some((item) => item.source === 'dadata')) {
                        setProviders((p) => ({ ...p, dadataActive: true }));
                        setProviderStatus('active');
                    }
                }
            } catch {
                if (!cancelled) setSuggestions([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [debouncedQuery, city, api, disabled]);

    async function applySelection(item) {
        const full = item.fullAddress || item.title;
        let resolvedAddress = full;
        setQuery(full);
        setOpen(false);

        let newLat = item.lat != null ? Number(item.lat) : null;
        let newLng = item.lng != null ? Number(item.lng) : null;
        let region = null;
        let postalCode = null;
        let resolvedCity = city || null;
        if (item.source) setLastSource(item.source);

        const useDadataGeocode = item.source === 'dadata' || !item.source;
        if (useDadataGeocode && full.trim().length >= 3) {
            try {
                const geo = await api.get('/address/geocode', { params: { address: full } });
                if (geo.data?.success && geo.data.lat != null && geo.data.lng != null) {
                    const gLat = Number(geo.data.lat);
                    const gLng = Number(geo.data.lng);
                    const hadPin = newLat != null && newLng != null;
                    const shiftM = hadPin ? haversineMeters(newLat, newLng, gLat, gLng) : 0;
                    if (!hadPin || shiftM <= 35) {
                        newLat = gLat;
                        newLng = gLng;
                    }
                    region = geo.data.region;
                    postalCode = geo.data.postalCode;
                    if (geo.data.city) resolvedCity = geo.data.city;
                    if (geo.data.source) setLastSource(geo.data.source);
                    if (geo.data.formattedAddress) {
                        resolvedAddress = geo.data.formattedAddress;
                        setQuery(resolvedAddress);
                    }
                }
            } catch { /* ignore */ }
        } else if (newLat == null || newLng == null) {
            try {
                const geo = await api.get('/address/geocode', { params: { address: full } });
                if (geo.data?.success) {
                    newLat = geo.data.lat != null ? Number(geo.data.lat) : null;
                    newLng = geo.data.lng != null ? Number(geo.data.lng) : null;
                    region = geo.data.region;
                    postalCode = geo.data.postalCode;
                    if (geo.data.city) resolvedCity = geo.data.city;
                    if (geo.data.source) setLastSource(geo.data.source);
                }
            } catch { /* ignore */ }
        }

        setMapLat(newLat);
        setMapLng(newLng);
        if (newLat != null && newLng != null)
            setMapCenter([newLat, newLng]);
        onChange?.({
            address: resolvedAddress,
            addressFull: resolvedAddress,
            lat: newLat,
            lng: newLng,
            city: resolvedCity,
            region,
            postalCode
        });
    }

    function handleMarkerMove(newLat, newLng) {
        setMapLat(newLat);
        setMapLng(newLng);
        setMapCenter([newLat, newLng]);
        if (reverseTimerRef.current)
            clearTimeout(reverseTimerRef.current);
        const seq = ++reverseSeqRef.current;
        reverseTimerRef.current = setTimeout(() => runReverseGeocode(newLat, newLng, seq), 400);
    }

    async function runReverseGeocode(clickLat, clickLng, seq) {
        const pinLat = Number(clickLat);
        const pinLng = Number(clickLng);
        try {
            const res = await api.get('/address/reverse', {
                params: { lat: pinLat, lng: pinLng }
            });
            if (seq !== reverseSeqRef.current)
                return;
            setMapLat(pinLat);
            setMapLng(pinLng);
            setMapCenter([pinLat, pinLng]);
            if (res.data?.success && res.data.formattedAddress) {
                setReverseHint('');
                let full = res.data.formattedAddress;
                const lowPrecision = !!res.data.lowPrecision;
                if (res.data.source) setLastSource(res.data.source);
                setStreetLine({
                    street: res.data.street || null,
                    houseNumber: res.data.houseNumber || null
                });
                if (lowPrecision) {
                    setReverseHint(t('addressInput.reverseLowPrecision'));
                    const manual = (query || address || '').trim();
                    if (manual.length > 3 && !full.toLowerCase().includes(manual.toLowerCase().slice(0, 8)))
                        full = `${manual}, ${full}`;
                } else {
                    setReverseHint('');
                }
                suppressSuggestRef.current = true;
                setQuery(full);
                onChange?.({
                    address: full,
                    addressFull: full,
                    lat: pinLat,
                    lng: pinLng,
                    city: res.data.city || city,
                    region: res.data.region
                });
                return;
            }
            const err = res.data?.error;
            if (err === 'dadata_not_found' || err === 'not_found') {
                setReverseHint(t('addressInput.reverseDadataNotFound'));
            } else if (res.data?.source === 'photon') {
                setReverseHint(t('addressInput.reversePhotonOnly'));
            } else if (err === 'geocoder_error') {
                setReverseHint(t('addressInput.reverseGeocoderError'));
            } else if (err) {
                setReverseHint(t('addressInput.reverseNotFound'));
            }
        } catch {
            if (seq !== reverseSeqRef.current) return;
            setReverseHint(t('addressInput.reverseNetworkError'));
        }
        if (seq !== reverseSeqRef.current) return;
        onChange?.({
            address: query,
            addressFull: query,
            lat: pinLat,
            lng: pinLng,
            city
        });
    }

    useEffect(() => () => {
        if (reverseTimerRef.current)
            clearTimeout(reverseTimerRef.current);
    }, []);

    const hasCoords = mapLat != null && mapLng != null;
    const mapHint = hasCoords
        ? t('addressInput.mapHintWithCoords')
        : t('addressInput.mapHintNoCoords');

    const focusStyle = {
        borderColor: '#2563eb',
        outline: 'none',
        boxShadow: '0 0 0 2px rgba(37,99,235,0.15)'
    };

    const suggestOpen = open && suggestions.length > 0;

    return (
        <div ref={wrapRef}>
            <div
                style={{
                    position: 'relative',
                    zIndex: suggestOpen ? 5000 : 2
                }}
            >
            <AddressProvidersBanner
                status={providerStatus}
                photonConfigured={!!providers.photonConfigured}
            />
            <div style={{ position: 'relative' }}>
            <input
                type="text"
                value={query}
                disabled={disabled}
                placeholder={inputPlaceholder}
                onChange={(e) => {
                    setQuery(e.target.value);
                    onChange?.({ address: e.target.value, lat: mapLat, lng: mapLng, city });
                }}
                onFocus={(e) => {
                    Object.assign(e.target.style, focusStyle);
                    if (suggestions.length) setOpen(true);
                }}
                onBlur={(e) => {
                    e.target.style.borderColor = inputStyle.borderColor || 'var(--sp-gray-200)';
                    e.target.style.boxShadow = 'none';
                }}
                style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: '1px solid var(--sp-gray-200)',
                    fontSize: 14,
                    boxSizing: 'border-box',
                    ...inputStyle
                }}
            />
            {suggestOpen && (
                <div style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: '100%',
                    marginTop: 4,
                    background: '#fff',
                    border: '1px solid var(--sp-gray-200)',
                    borderRadius: 8,
                    boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
                    zIndex: 5001,
                    maxHeight: 280,
                    overflowY: 'auto'
                }}>
                    {suggestions.map((item, idx) => (
                        <button
                            key={`${item.fullAddress}-${idx}`}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => applySelection(item)}
                            style={{
                                display: 'block',
                                width: '100%',
                                textAlign: 'left',
                                padding: '10px 12px',
                                border: 'none',
                                borderBottom: idx < suggestions.length - 1 ? '1px solid #f1f5f9' : 'none',
                                background: '#fff',
                                cursor: 'pointer',
                                fontSize: 13
                            }}
                        >
                            <div style={{ fontWeight: 600, color: '#1e293b', display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                                <span>{item.title}</span>
                                {item.source && <AddressSourceBadge source={item.source} compact />}
                            </div>
                            {item.subtitle && (
                                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{item.subtitle}</div>
                            )}
                        </button>
                    ))}
                </div>
            )}
            </div>
            {loading && (
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{t('addressInput.searching')}</div>
            )}
            {geocodingMap && (
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{t('addressInput.geocodingMap')}</div>
            )}
            {reverseHint && (
                <div style={{ fontSize: 11, color: '#b45309', marginTop: 4 }}>{reverseHint}</div>
            )}
            {hasCoords && (
                <div style={{ fontSize: 11, color: '#16a34a', marginTop: 4, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                    <span>📍 {Number(mapLat).toFixed(7)}, {Number(mapLng).toFixed(7)}</span>
                    {lastSource && <AddressSourceBadge source={lastSource} compact />}
                </div>
            )}
            <StreetHouseLine street={streetLine.street} houseNumber={streetLine.houseNumber} />
            </div>
            <div style={{
                marginTop: 8,
                position: 'relative',
                zIndex: suggestOpen ? 0 : 1,
                pointerEvents: suggestOpen ? 'none' : 'auto',
                opacity: suggestOpen ? 0.55 : 1,
                transition: 'opacity 0.15s ease'
            }}>
                <LocationMap
                    lat={mapLat}
                    lng={mapLng}
                    draggable={!disabled && !suggestOpen}
                    onCoordinatesChange={handleMarkerMove}
                    alwaysShow
                    defaultCenter={mapCenter}
                    defaultZoom={hasCoords ? 16 : 11}
                    allowMapClick={!disabled && !suggestOpen}
                    hint={mapHint}
                />
            </div>
        </div>
    );
}
